import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Tabs,
  Tag,
  Space,
  message,
  Spin,
  Typography,
  Row,
  Col,
  Select,
  Table,
  Modal,
  InputNumber,
  Popconfirm,
  Alert,
  Divider,
  Steps,
  Segmented,
  Switch,
} from 'antd';
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  SaveOutlined,
  ThunderboltOutlined,
  CheckOutlined,
  HolderOutlined,
  TrophyOutlined,
  TeamOutlined,
  ApartmentOutlined,
  UnorderedListOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
import TorneoBracket, { buildPreviewPorRonda } from '../../../components/torneos/TorneoBracket';
import LlaveOctavosEditor from '../../../components/torneos/LlaveOctavosEditor';

const { Title, Text } = Typography;

const FASE_LABELS = {
  INSCRIPCION: 'Inscripción',
  ARMADO_GRUPOS: 'Armado de grupos',
  FASE_GRUPOS: 'Fase de grupos',
  LLAVE: 'Llave eliminatoria',
  FINALIZADO: 'Finalizado',
};

const RONDA_LABELS = {
  '16VOS': '16avos',
  '8VOS': 'Octavos',
  '4TOS': 'Cuartos',
  SEMIS: 'Semifinales',
  FINAL: 'Final',
};

const crearSetsVacios = (cantidadSets) => {
  const total = cantidadSets === 1 ? 1 : 3;
  return Array.from({ length: total }, (_, i) => ({
    numeroSet: i + 1,
    gamesA: null,
    gamesB: null,
  }));
};

const esScoreSetRegularValido = (ganador, perdedor) => {
  if (ganador === 6 && perdedor <= 4) return true;
  if (ganador === 7 && (perdedor === 5 || perdedor === 6)) return true;
  return false;
};

const esSuperTieBreakValido = (ganador, perdedor) => ganador >= 10 && ganador - perdedor >= 2;

const esSuperTieBreakSet = (numeroSet, cantidadSets, superTieBreak = true) =>
  cantidadSets === 3 && numeroSet === 3 && superTieBreak !== false;

const validarScoreSetPadel = (gamesA, gamesB, numeroSet, cantidadSets, superTieBreak = true) => {
  if (gamesA == null || gamesB == null) {
    return { valido: true, mensaje: null };
  }

  if (!Number.isInteger(gamesA) || !Number.isInteger(gamesB) || gamesA < 0 || gamesB < 0) {
    return { valido: false, mensaje: 'Los games deben ser números enteros positivos' };
  }

  if (gamesA === gamesB) {
    return { valido: false, mensaje: 'El set no puede terminar empatado' };
  }

  const ganador = Math.max(gamesA, gamesB);
  const perdedor = Math.min(gamesA, gamesB);
  const score = `${gamesA}-${gamesB}`;

  if (esSuperTieBreakSet(numeroSet, cantidadSets, superTieBreak)) {
    if (!esSuperTieBreakValido(ganador, perdedor)) {
      return {
        valido: false,
        mensaje: `Super tie-break inválido (${score}). Debe llegar a 10 con diferencia de 2`,
      };
    }
    return { valido: true, mensaje: null };
  }

  if (!esScoreSetRegularValido(ganador, perdedor)) {
    if (ganador === 6 && perdedor === 5) {
      return {
        valido: false,
        mensaje: `Set incompleto (${score}). Con 6-5 debe seguir hasta 7-5 o 7-6`,
      };
    }
    if (ganador < 6) {
      return {
        valido: false,
        mensaje: `Score inválido (${score}). El ganador necesita al menos 6 games`,
      };
    }
    return {
      valido: false,
      mensaje: `Score inválido (${score}). Válidos: 6-0 a 6-4, 7-5 o 7-6`,
    };
  }

  return { valido: true, mensaje: null };
};

const calcularResumenSets = (setsDetalle, cantidadSets, superTieBreak = true) => {
  let setsA = 0;
  let setsB = 0;
  for (const s of setsDetalle) {
    if (s.gamesA == null || s.gamesB == null) continue;
    const validacion = validarScoreSetPadel(s.gamesA, s.gamesB, s.numeroSet, cantidadSets, superTieBreak);
    if (!validacion.valido) continue;
    if (s.gamesA > s.gamesB) setsA += 1;
    else if (s.gamesB > s.gamesA) setsB += 1;
  }
  return { setsA, setsB };
};

const validarResultadoPartidoCompleto = (setsDetalle, cantidadSets, superTieBreak = true) => {
  const { setsA, setsB } = calcularResumenSets(setsDetalle, cantidadSets, superTieBreak);
  if (cantidadSets === 3 && setsA === 1 && setsB === 1) {
    const set3 = setsDetalle.find((s) => s.numeroSet === 3);
    if (!set3 || set3.gamesA == null || set3.gamesB == null) {
      return { valido: false, mensaje: 'Falta el set 3 — el partido está 1-1' };
    }
  }
  return { valido: true, mensaje: null };
};

const detalleSetsDesdePartido = (partido, cantidadSets) => {
  const vacios = crearSetsVacios(cantidadSets);
  if (!partido?.detalle_sets?.length) return vacios;
  const map = new Map(
    partido.detalle_sets.map((s) => [s.numero_set, { gamesA: s.games_a, gamesB: s.games_b }])
  );
  return vacios.map((s) => ({
    ...s,
    gamesA: map.get(s.numeroSet)?.gamesA ?? null,
    gamesB: map.get(s.numeroSet)?.gamesB ?? null,
  }));
};

const formatearResultadoTabla = (partido, campoEstado, setsA, setsB) => {
  if (partido[campoEstado] !== 'JUGADO' && partido[campoEstado] !== 'WO') return '-';
  return partido.resultado_formateado || `${setsA} - ${setsB}`;
};

const AUTO_SCROLL_MARGIN = 72;
const AUTO_SCROLL_SPEED = 18;

const parejaAsignableAGrupo = (pareja) => pareja?.tni_estado !== 'PENDIENTE';

const ParejaCard = ({ pareja, draggable, onDragStart, onDragEnd, isDragging, selected, onSelect }) => {
  const wasDragged = React.useRef(false);
  const puedeArrastrar = draggable && parejaAsignableAGrupo(pareja);
  const pendientePago = pareja.tni_estado === 'PENDIENTE';

  return (
  <div
    draggable={puedeArrastrar}
    onDragStart={(e) => {
      if (!puedeArrastrar) {
        e.preventDefault();
        return;
      }
      wasDragged.current = true;
      onDragStart?.(e);
    }}
    onDragEnd={(e) => {
      onDragEnd?.(e);
      setTimeout(() => { wasDragged.current = false; }, 0);
    }}
    onClick={(e) => {
      e.stopPropagation();
      if (wasDragged.current || !onSelect) return;
      onSelect?.(pareja);
    }}
    style={{
      padding: '8px 10px',
      marginBottom: 6,
      background: isDragging || selected ? '#404040' : '#2d2d2d',
      border: selected ? '1px solid #c4ff00' : '1px solid #434343',
      borderRadius: 8,
      cursor: pendientePago ? 'not-allowed' : (puedeArrastrar ? 'grab' : 'default'),
      opacity: pendientePago ? 0.72 : 1,
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      fontSize: 13,
    }}
  >
    {puedeArrastrar && <HolderOutlined style={{ color: '#888' }} />}
    <span style={{ flex: 1 }}>{pareja.etiqueta}</span>
    {pendientePago && (
      <Tag color="orange" style={{ margin: 0 }}>Pendiente</Tag>
    )}
  </div>
  );
};

const ZonaDrop = ({
  titulo,
  parejas,
  cupo,
  grupoKey,
  semilla,
  onSemillaChange,
  editable,
  onDrop,
  dragItem,
  setDragItem,
  onDragOverZone,
  selectedPareja,
  onSelectPareja,
  highlightDrop,
}) => {
  const handleDragOver = (e) => {
    e.preventDefault();
    onDragOverZone?.(e.clientY);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (!editable) return;
    if (dragItem) {
      onDrop(dragItem, grupoKey);
      setDragItem(null);
      return;
    }
    if (selectedPareja) {
      onDrop({ ...selectedPareja, from: selectedPareja.from || 'sin-asignar' }, grupoKey);
      onSelectPareja?.(null);
    }
  };

  const handleClick = () => {
    if (!editable || !selectedPareja) return;
    onDrop({ ...selectedPareja, from: selectedPareja.from || 'sin-asignar' }, grupoKey);
    onSelectPareja?.(null);
  };

  const count = parejas.length;
  const lleno = cupo != null && count >= cupo;

  return (
    <div
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={handleClick}
      style={{
        minHeight: 120,
        padding: 12,
        background: highlightDrop ? '#2a2a1a' : '#1f1f1f',
        border: highlightDrop ? '2px dashed #c4ff00' : '2px dashed #434343',
        borderRadius: 10,
        marginBottom: 12,
        transition: 'border-color 0.15s, background 0.15s',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8 }}>
        <Text strong>{titulo}</Text>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {onSemillaChange && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Sem.</Text>
              <InputNumber
                min={1}
                max={99}
                size="small"
                value={semilla ?? undefined}
                disabled={!editable}
                placeholder="—"
                onClick={(e) => e.stopPropagation()}
                onChange={(v) => onSemillaChange(grupoKey, v)}
                style={{ width: 52 }}
              />
            </div>
          )}
          {cupo != null && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {count}/{cupo}
            </Text>
          )}
        </div>
      </div>
      {parejas.map((p) => (
        <ParejaCard
          key={p.tni_id}
          pareja={p}
          draggable={editable}
          isDragging={dragItem?.tni_id === p.tni_id}
          selected={selectedPareja?.tni_id === p.tni_id}
          onSelect={editable && parejaAsignableAGrupo(p) ? (pareja) => onSelectPareja?.(
            selectedPareja?.tni_id === pareja.tni_id
              ? null
              : { ...pareja, from: grupoKey }
          ) : undefined}
          onDragStart={() => {
            if (!parejaAsignableAGrupo(p)) return;
            setDragItem({ ...p, from: grupoKey });
          }}
          onDragEnd={() => setDragItem(null)}
        />
      ))}
      {editable && !lleno && count === 0 && (
        <Text type="secondary" style={{ fontSize: 12 }}>Arrastrá parejas aquí</Text>
      )}
    </div>
  );
};

const TorneoGestion = () => {
  const { torneoId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resumen, setResumen] = useState(null);
  const [armado, setArmado] = useState(null);
  const [faseGrupos, setFaseGrupos] = useState(null);
  const [llave, setLlave] = useState(null);
  const [preview, setPreview] = useState(null);
  const [crucesLocal, setCrucesLocal] = useState(null);
  const [crucesDirty, setCrucesDirty] = useState(false);
  const [activeTab, setActiveTab] = useState('grupos');
  const [clasificadosPorGrupo, setClasificadosPorGrupo] = useState(2);

  const [gruposLocal, setGruposLocal] = useState([]);
  const [sinAsignarLocal, setSinAsignarLocal] = useState([]);
  const [dragItem, setDragItem] = useState(null);
  const [selectedPareja, setSelectedPareja] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [cambiandoInscripcion, setCambiandoInscripcion] = useState(false);
  const [modalInscripcionManual, setModalInscripcionManual] = useState(false);
  const [jugadores, setJugadores] = useState([]);
  const [jugador1Id, setJugador1Id] = useState(null);
  const [jugador2Id, setJugador2Id] = useState(null);
  const [marcadaPagada, setMarcadaPagada] = useState(false);
  const [formaPagoId, setFormaPagoId] = useState(null);
  const [jugadorClubIdCobro, setJugadorClubIdCobro] = useState(null);
  const [formasPago, setFormasPago] = useState([]);
  const [guardandoInscripcion, setGuardandoInscripcion] = useState(false);
  const [cargandoJugadores, setCargandoJugadores] = useState(false);

  const [modalResultado, setModalResultado] = useState(null);
  const [setsDetalle, setSetsDetalle] = useState([]);
  const [vistaLlave, setVistaLlave] = useState('bracket');

  const cantidadSetsTorneo = resumen?.torneo?.tor_cantidad_sets
    ?? faseGrupos?.torneo?.tor_cantidad_sets
    ?? llave?.torneo?.tor_cantidad_sets
    ?? 3;

  const superTieBreakTorneo = (resumen?.torneo?.tor_super_tie_break
    ?? faseGrupos?.torneo?.tor_super_tie_break
    ?? llave?.torneo?.tor_super_tie_break) !== false;

  const columnasTablaGrupo = [
    { title: 'Pareja', dataIndex: 'etiqueta', key: 'etiqueta' },
    { title: 'PJ', dataIndex: 'pj', width: 50 },
    { title: 'PG', dataIndex: 'pg', width: 50 },
    { title: 'PP', dataIndex: 'pp', width: 50 },
    { title: 'Pts', dataIndex: 'puntos', width: 50 },
    { title: 'Sets +/-', key: 'diff_sets', width: 80, render: (_, r) => r.sets_favor - r.sets_contra },
    { title: 'Games +/-', key: 'diff_games', width: 90, render: (_, r) => r.games_favor - r.games_contra },
    ...(superTieBreakTorneo && cantidadSetsTorneo === 3
      ? [{
        title: 'Super TB +/-',
        key: 'diff_stb',
        width: 100,
        render: (_, r) => (r.stb_favor ?? 0) - (r.stb_contra ?? 0),
      }]
      : []),
  ];

  const resumenSetsModal = calcularResumenSets(setsDetalle, cantidadSetsTorneo, superTieBreakTorneo);

  const validacionesSetsModal = setsDetalle.map((set) => ({
    numeroSet: set.numeroSet,
    ...validarScoreSetPadel(set.gamesA, set.gamesB, set.numeroSet, cantidadSetsTorneo, superTieBreakTorneo),
  }));

  const haySetsConError = validacionesSetsModal.some(
    (v) => !v.valido && setsDetalle.find((s) => s.numeroSet === v.numeroSet)?.gamesA != null
      && setsDetalle.find((s) => s.numeroSet === v.numeroSet)?.gamesB != null
  );

  const validacionPartidoCompleto = validarResultadoPartidoCompleto(
    setsDetalle,
    cantidadSetsTorneo,
    superTieBreakTorneo
  );
  const partidoIncompleto = !validacionPartidoCompleto.valido;

  const editableGrupos = resumen && !resumen.torneo.tor_grupos_confirmados;

  const autoScrollOnDrag = useCallback((clientY) => {
    if (clientY < AUTO_SCROLL_MARGIN) {
      window.scrollBy(0, -AUTO_SCROLL_SPEED);
    } else if (clientY > window.innerHeight - AUTO_SCROLL_MARGIN) {
      window.scrollBy(0, AUTO_SCROLL_SPEED);
    }

    document.querySelectorAll('[data-drag-scroll]').forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (clientY < rect.top + AUTO_SCROLL_MARGIN) {
        el.scrollTop -= AUTO_SCROLL_SPEED;
      } else if (clientY > rect.bottom - AUTO_SCROLL_MARGIN) {
        el.scrollTop += AUTO_SCROLL_SPEED;
      }
    });
  }, []);

  useEffect(() => {
    if (!dragItem) return undefined;

    let rafId = null;
    let lastY = 0;

    const onDragOver = (e) => {
      e.preventDefault();
      lastY = e.clientY;
      if (rafId == null) {
        rafId = requestAnimationFrame(function tick() {
          autoScrollOnDrag(lastY);
          rafId = requestAnimationFrame(tick);
        });
      }
    };

    const stopScroll = () => {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    document.addEventListener('dragover', onDragOver);
    document.addEventListener('drop', stopScroll);
    document.addEventListener('dragend', stopScroll);

    return () => {
      stopScroll();
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('drop', stopScroll);
      document.removeEventListener('dragend', stopScroll);
    };
  }, [dragItem, autoScrollOnDrag]);

  const loadResumen = useCallback(async () => {
    const res = await apiClient.get(`/torneos/gestion/${torneoId}/resumen`);
    if (res.ok) setResumen(await res.json());
  }, [torneoId]);

  const loadArmado = useCallback(async () => {
    const res = await apiClient.get(`/torneos/gestion/${torneoId}/grupos`);
    if (res.ok) {
      const data = await res.json();
      setArmado(data);
      setGruposLocal(data.grupos || []);
      setSinAsignarLocal(data.parejas_sin_asignar || []);
      setClasificadosPorGrupo(data.torneo.tor_clasificados_por_grupo ?? 2);
      setDirty(false);
    }
  }, [torneoId]);

  const loadFaseGrupos = useCallback(async () => {
    const res = await apiClient.get(`/torneos/gestion/${torneoId}/fase-grupos`);
    if (res.ok) setFaseGrupos(await res.json());
  }, [torneoId]);

  const loadLlave = useCallback(async () => {
    const llaveRes = await apiClient.get(`/torneos/gestion/${torneoId}/llave`);
    if (llaveRes.ok) {
      setLlave(await llaveRes.json());
    } else {
      setLlave(null);
    }

    const previewRes = await apiClient.get(`/torneos/gestion/${torneoId}/llave/preview`);
    if (previewRes.ok) {
      const data = await previewRes.json();
      setPreview(data);
      setCrucesLocal(data.cruces_primera_ronda ?? null);
      setCrucesDirty(false);
      return;
    }

    setPreview(null);
    setCrucesLocal(null);
    setCrucesDirty(false);
  }, [torneoId]);

  const loadAll = async () => {
    setLoading(true);
    try {
      await Promise.all([loadResumen(), loadArmado(), loadFaseGrupos(), loadLlave()]);
    } catch (e) {
      console.error(e);
      message.error('Error al cargar datos del torneo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [torneoId]);

  const aplicarSugerencia = () => {
    if (!armado?.sugerencia?.grupos?.length) {
      message.warning('No hay sugerencia disponible para la cantidad de parejas');
      return;
    }
    const map = new Map();
    [...sinAsignarLocal, ...gruposLocal.flatMap((g) => g.parejas)].forEach((p) => {
      map.set(p.tni_id, p);
    });
    const nuevosGrupos = armado.sugerencia.grupos.map((g) => ({
      tgr_nombre: g.nombre,
      tgr_orden: g.orden,
      tgr_cupo: g.cupo,
      tgr_semilla: g.orden,
      parejas: [],
    }));
    setGruposLocal(nuevosGrupos);
    setSinAsignarLocal(Array.from(map.values()));
    setDirty(true);
    message.info(armado.sugerencia.mensaje);
  };

  const handleDrop = (item, destinoKey) => {
    if (!editableGrupos) return;

    if (destinoKey !== 'sin-asignar' && !parejaAsignableAGrupo(item)) {
      message.warning('Esta pareja tiene el pago pendiente. No se puede asignar a un grupo hasta que abone la inscripción.');
      return;
    }

    let nuevaSinAsignar = [...sinAsignarLocal];
    let nuevosGrupos = gruposLocal.map((g) => ({ ...g, parejas: [...g.parejas] }));

    const sacarDeOrigen = () => {
      if (item.from === 'sin-asignar') {
        nuevaSinAsignar = nuevaSinAsignar.filter((p) => p.tni_id !== item.tni_id);
      } else {
        nuevosGrupos = nuevosGrupos.map((g) => {
          if (g.tgr_nombre === item.from) {
            return { ...g, parejas: g.parejas.filter((p) => p.tni_id !== item.tni_id) };
          }
          return g;
        });
      }
    };

    sacarDeOrigen();

    const pareja = { tni_id: item.tni_id, etiqueta: item.etiqueta, tni_estado: item.tni_estado };

    if (destinoKey === 'sin-asignar') {
      nuevaSinAsignar.push(pareja);
    } else {
      const idx = nuevosGrupos.findIndex((g) => g.tgr_nombre === destinoKey);
      if (idx === -1) return;
      if (nuevosGrupos[idx].parejas.length >= nuevosGrupos[idx].tgr_cupo) {
        message.warning(`El grupo ${destinoKey} está lleno`);
        return;
      }
      nuevosGrupos[idx].parejas.push(pareja);
    }

    setSinAsignarLocal(nuevaSinAsignar);
    setGruposLocal(nuevosGrupos);
    setDirty(true);
    setSelectedPareja(null);
  };

  const cambiarSemillaGrupo = (grupoKey, valor) => {
    if (!editableGrupos) return;
    setGruposLocal((prev) => prev.map((g) => (
      g.tgr_nombre === grupoKey
        ? { ...g, tgr_semilla: valor == null ? null : Number(valor) }
        : g
    )));
    setDirty(true);
  };

  const guardarGrupos = async () => {
    setSaving(true);
    try {
      const res = await apiClient.put(`/torneos/gestion/${torneoId}/grupos`, {
        grupos: gruposLocal,
        parejasSinAsignar: sinAsignarLocal,
        clasificadosPorGrupo,
      });
      if (res.ok) {
        message.success('Grupos guardados');
        const data = await res.json();
        setArmado(data);
        setDirty(false);
        await loadResumen();
      } else {
        const err = await res.json();
        message.error(err.message || 'Error al guardar');
      }
    } catch (e) {
      message.error('Error al guardar grupos');
    } finally {
      setSaving(false);
    }
  };

  const sorteoAutomatico = async (modo) => {
    setSaving(true);
    try {
      if (dirty) await guardarGrupos();
      const res = await apiClient.post(`/torneos/gestion/${torneoId}/grupos/sorteo`, { modo });
      if (res.ok) {
        message.success(
          modo === 'snake'
            ? 'Sorteo snake aplicado. Se mantuvieron las parejas ya ubicadas en los grupos.'
            : 'Sorteo aleatorio aplicado. Se mantuvieron las parejas ya ubicadas en los grupos.'
        );
        await loadArmado();
        await loadResumen();
      } else {
        const err = await res.json();
        message.error(err.message || 'Error en sorteo');
      }
    } finally {
      setSaving(false);
    }
  };

  const confirmarGrupos = async () => {
    if (dirty) await guardarGrupos();
    setSaving(true);
    try {
      const res = await apiClient.post(`/torneos/gestion/${torneoId}/grupos/confirmar`);
      if (res.ok) {
        message.success('Grupos confirmados. Se generaron los partidos de zona.');
        await loadAll();
        setActiveTab('fase-grupos');
      } else {
        const err = await res.json();
        message.error(err.message || 'Error al confirmar');
      }
    } finally {
      setSaving(false);
    }
  };

  const abrirResultadoGrupo = (partido) => {
    setModalResultado({ tipo: 'grupo', partido });
    setSetsDetalle(detalleSetsDesdePartido(partido, cantidadSetsTorneo));
  };

  const abrirResultadoLlave = (partido) => {
    setModalResultado({ tipo: 'llave', partido });
    setSetsDetalle(detalleSetsDesdePartido(partido, cantidadSetsTorneo));
  };

  const actualizarSetDetalle = (numeroSet, campo, valor) => {
    setSetsDetalle((prev) => prev.map((s) => (
      s.numeroSet === numeroSet ? { ...s, [campo]: valor } : s
    )));
  };

  const guardarResultado = async () => {
    if (!modalResultado) return;
    const { tipo, partido } = modalResultado;
    const url = tipo === 'grupo'
      ? `/torneos/gestion/partidos-grupo/${partido.tpg_id}/resultado`
      : `/torneos/gestion/partidos-llave/${partido.tpl_id}/resultado`;

    const setsConGames = setsDetalle.filter((s) => s.gamesA != null && s.gamesB != null);
    if (setsConGames.length === 0) {
      message.warning('Ingresá los games de al menos un set');
      return;
    }

    const errores = setsConGames
      .map((s) => validarScoreSetPadel(s.gamesA, s.gamesB, s.numeroSet, cantidadSetsTorneo, superTieBreakTorneo))
      .filter((v) => !v.valido);
    if (errores.length > 0) {
      message.error(errores[0].mensaje);
      return;
    }

    const validacionCompleta = validarResultadoPartidoCompleto(
      setsDetalle,
      cantidadSetsTorneo,
      superTieBreakTorneo
    );
    if (!validacionCompleta.valido) {
      message.error(validacionCompleta.mensaje);
      return;
    }

    setSaving(true);
    try {
      const res = await apiClient.put(url, { setsDetalle: setsConGames });
      if (res.ok) {
        message.success('Resultado guardado');
        setModalResultado(null);
        if (tipo === 'grupo') {
          setFaseGrupos(await res.json());
        } else {
          setLlave(await res.json());
          await loadResumen();
        }
      } else {
        const err = await res.json();
        message.error(err.message || 'Error al guardar resultado');
      }
    } finally {
      setSaving(false);
    }
  };

  const cerrarFaseGrupos = async () => {
    setSaving(true);
    try {
      const res = await apiClient.post(`/torneos/gestion/${torneoId}/fase-grupos/cerrar`);
      if (res.ok) {
        message.success('Fase de grupos cerrada. Ya podés generar la llave.');
        await loadAll();
        setActiveTab('llave');
      } else {
        const err = await res.json();
        message.error(err.message || 'Error al cerrar fase');
      }
    } finally {
      setSaving(false);
    }
  };

  const generarLlave = async () => {
    if (!crucesLocal?.length) {
      message.warning('No hay cruces para generar la llave');
      return;
    }
    setSaving(true);
    try {
      const res = await apiClient.post(`/torneos/gestion/${torneoId}/llave/generar`, {
        cruces_primera_ronda: crucesLocal,
      });
      if (res.ok) {
        message.success('Llave generada');
        setLlave(await res.json());
        setCrucesDirty(false);
        await loadResumen();
      } else {
        const err = await res.json();
        message.error(err.message || 'Error al generar llave');
      }
    } finally {
      setSaving(false);
    }
  };

  const restaurarCrucesAutomaticos = () => {
    if (!preview?.cruces_primera_ronda) return;
    setCrucesLocal(preview.cruces_primera_ronda);
    setCrucesDirty(false);
    message.info('Cuadro restaurado al armado automático');
  };

  const handleCrucesChange = (nuevosCruces) => {
    setCrucesLocal(nuevosCruces);
    setCrucesDirty(true);
  };

  const handleInscripcionAbierta = async (abierta) => {
    setCambiandoInscripcion(true);
    try {
      const res = await apiClient.patch(`/torneos/gestion/${torneoId}/inscripcion-abierta`, { abierta });
      const data = await res.json();
      if (!res.ok) {
        message.error(data.message || 'Error al actualizar estado de inscripción');
        return;
      }
      message.success(data.message);
      setResumen((prev) => ({
        ...prev,
        torneo: { ...prev.torneo, tor_inscripcion_abierta: abierta },
      }));
    } catch (e) {
      console.error(e);
      message.error('Error al actualizar estado de inscripción');
    } finally {
      setCambiandoInscripcion(false);
    }
  };

  const abrirModalInscripcionManual = async () => {
    setJugador1Id(null);
    setJugador2Id(null);
    setMarcadaPagada(false);
    setFormaPagoId(null);
    setJugadorClubIdCobro(null);
    setModalInscripcionManual(true);
    setCargandoJugadores(true);
    try {
      const [jugRes, fpRes] = await Promise.all([
        jugadores.length > 0
          ? Promise.resolve({ ok: true, json: async () => jugadores })
          : apiClient.get('/club/jugadores'),
        formasPago.length > 0
          ? Promise.resolve({ ok: true, json: async () => formasPago })
          : apiClient.get(`/torneos/gestion/${torneoId}/formas-pago`),
      ]);
      if (jugRes.ok && jugadores.length === 0) {
        const data = await jugRes.json();
        setJugadores(Array.isArray(data) ? data : []);
      } else if (!jugRes.ok) {
        message.error('Error al cargar jugadores');
      }
      if (fpRes.ok && formasPago.length === 0) {
        const data = await fpRes.json();
        setFormasPago(Array.isArray(data) ? data : []);
      } else if (!fpRes.ok) {
        message.error('Error al cargar formas de pago');
      }
    } catch (e) {
      console.error(e);
      message.error('Error al cargar datos para inscripción');
    } finally {
      setCargandoJugadores(false);
    }
  };

  const guardarInscripcionManual = async () => {
    if (!jugador1Id || !jugador2Id) {
      message.warning('Seleccioná los dos jugadores de la pareja');
      return;
    }
    if (Number(jugador1Id) === Number(jugador2Id)) {
      message.warning('Los dos jugadores deben ser distintos');
      return;
    }
    if (marcadaPagada && !formaPagoId) {
      message.warning('Seleccioná la forma de pago');
      return;
    }
    const formaPago = formasPago.find((fp) => fp.fop_id === formaPagoId);
    const esCuentaCorriente = formaPago?.fop_nombre?.toUpperCase().trim() === 'CUENTA CORRIENTE';
    if (marcadaPagada && esCuentaCorriente && !jugadorClubIdCobro) {
      message.warning('Seleccioná el jugador de la cuenta corriente');
      return;
    }
    setGuardandoInscripcion(true);
    try {
      const res = await apiClient.post(`/torneos/gestion/${torneoId}/inscripciones-manual`, {
        jugadorId: jugador1Id,
        companeroId: jugador2Id,
        marcadaPagada,
        formaPagoId: marcadaPagada ? formaPagoId : null,
        jugadorClubIdCobro: marcadaPagada && esCuentaCorriente
          ? (jugadorClubIdCobro || jugador1Id)
          : null,
      });
      const data = await res.json();
      if (!res.ok) {
        message.error(data.message || 'Error al inscribir la pareja');
        return;
      }
      message.success(data.message || 'Pareja inscripta');
      setModalInscripcionManual(false);
      await loadAll();
    } catch (e) {
      console.error(e);
      message.error('Error al inscribir la pareja');
    } finally {
      setGuardandoInscripcion(false);
    }
  };

  const filterJugadorOption = (input, option) =>
    String(option?.label ?? option?.children ?? '')
      .toLowerCase()
      .includes((input || '').toLowerCase());

  const opcionesJugadores = jugadores.map((j) => ({
    value: j.jcl_id,
    label: `${j.jcl_nombre} ${j.jcl_apellido}${j.jcl_email ? ` (${j.jcl_email})` : ''}`,
  }));

  const formaPagoSeleccionada = formasPago.find((fp) => fp.fop_id === formaPagoId);
  const esCuentaCorrientePago = formaPagoSeleccionada?.fop_nombre?.toUpperCase().trim() === 'CUENTA CORRIENTE';
  const okInscripcionManual = Boolean(jugador1Id)
    && Boolean(jugador2Id)
    && Number(jugador1Id) !== Number(jugador2Id)
    && (!marcadaPagada || Boolean(formaPagoId))
    && (!marcadaPagada || !esCuentaCorrientePago || Boolean(jugadorClubIdCobro || jugador1Id));

  const faseActual = resumen?.torneo?.tor_fase || 'INSCRIPCION';
  const stepIndex = ['INSCRIPCION', 'ARMADO_GRUPOS', 'FASE_GRUPOS', 'LLAVE', 'FINALIZADO'].indexOf(faseActual);
  const gruposConfirmados = Boolean(resumen?.torneo?.tor_grupos_confirmados);
  const puedeGestionarInscripcion = !gruposConfirmados
    && (faseActual === 'INSCRIPCION' || faseActual === 'ARMADO_GRUPOS');
  const inscripcionCerrada = resumen?.torneo?.tor_inscripcion_abierta === false;
  const sinAsignarPagadas = sinAsignarLocal.filter(parejaAsignableAGrupo);
  const sinAsignarPendientes = sinAsignarLocal.filter((p) => !parejaAsignableAGrupo(p));
  const totalPagadas = armado?.total_pagadas ?? sinAsignarPagadas.length;

  if (loading && !resumen) {
    return (
      <MainLayout>
        <div style={{ textAlign: 'center', padding: 48 }}><Spin size="large" /></div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Card
        title={
          <Space>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/torneos/torneos')} />
            <TrophyOutlined />
            <span>Gestión: {resumen?.torneo?.tor_nombre}</span>
          </Space>
        }
        extra={
          <Space>
            <Tag color="blue">{FASE_LABELS[faseActual] || faseActual}</Tag>
            <Button icon={<ReloadOutlined />} onClick={loadAll} loading={loading}>Actualizar</Button>
          </Space>
        }
      >
        <Steps
          size="small"
          current={stepIndex}
          style={{ marginBottom: 24 }}
          items={[
            { title: 'Inscripción' },
            { title: 'Grupos' },
            { title: 'Zona' },
            { title: 'Llave' },
            { title: 'Fin' },
          ]}
        />

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}>
            <Text type="secondary">Fecha torneo: </Text>
            <Text>{resumen?.torneo?.tor_fecha ? dayjs(resumen.torneo.tor_fecha).format('DD/MM/YYYY') : '-'}</Text>
          </Col>
          <Col xs={24} sm={8}>
            <Text type="secondary">Parejas inscriptas: </Text>
            <Text strong>{resumen?.total_inscriptas ?? 0}</Text>
          </Col>
          <Col xs={24} sm={8}>
            <Text type="secondary">Grupos: </Text>
            <Text strong>{resumen?.total_grupos ?? 0}</Text>
          </Col>
        </Row>

        {puedeGestionarInscripcion && (
          <Alert
            type={resumen?.torneo?.tor_inscripcion_abierta !== false ? 'success' : 'warning'}
            showIcon
            style={{ marginBottom: 16 }}
            message={(
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <span>
                  <Text strong>Inscripciones: </Text>
                  <Text>
                    {resumen?.torneo?.tor_inscripcion_abierta !== false ? 'Torneo abierto' : 'Torneo cerrado'}
                  </Text>
                </span>
                <Space wrap>
                  <Button
                    icon={<UserAddOutlined />}
                    onClick={abrirModalInscripcionManual}
                  >
                    Inscribir pareja
                  </Button>
                  <Switch
                    checked={resumen?.torneo?.tor_inscripcion_abierta !== false}
                    loading={cambiandoInscripcion}
                    onChange={handleInscripcionAbierta}
                    checkedChildren="Abierto"
                    unCheckedChildren="Cerrado"
                  />
                </Space>
              </div>
            )}
            description={
              resumen?.torneo?.tor_inscripcion_abierta !== false
                ? 'Los jugadores pueden inscribirse desde Mis Torneos. También podés cargar una pareja manualmente (por ejemplo, si llaman por teléfono).'
                : 'Los jugadores verán que el torneo está cerrado. Todavía podés inscribir parejas manualmente desde acá.'
            }
          />
        )}

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'grupos',
              label: <span><TeamOutlined /> Armado de grupos</span>,
              children: (
                <div>
                  {armado?.sugerencia?.mensaje && (
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message={`Sugerencia (grupos de ${armado?.torneo?.tor_tamano_grupo ?? 4}): ${armado.sugerencia.mensaje} (${armado.total_inscriptas} inscriptas, ${totalPagadas} confirmadas)`}
                      description="Podés crear la estructura de grupos vacía e ir asignando solo las parejas con pago confirmado."
                    />
                  )}

                  <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col>
                      <Text>Clasificados por grupo: </Text>
                      <Select
                        value={clasificadosPorGrupo}
                        disabled={!editableGrupos}
                        onChange={(v) => { setClasificadosPorGrupo(v); setDirty(true); }}
                        options={[
                          { value: 1, label: 'Solo 1°' },
                          { value: 2, label: '1° y 2°' },
                        ]}
                        style={{ width: 140 }}
                      />
                    </Col>
                    {editableGrupos && (
                      <Col flex="auto">
                        <Space wrap>
                          <Button onClick={aplicarSugerencia}>Crear estructura sugerida</Button>
                          <Button icon={<ThunderboltOutlined />} onClick={() => sorteoAutomatico('aleatorio')} loading={saving}>
                            Sorteo aleatorio
                          </Button>
                          <Button onClick={() => sorteoAutomatico('snake')} loading={saving}>
                            Sorteo snake
                          </Button>
                          <Button type="primary" icon={<SaveOutlined />} onClick={guardarGrupos} loading={saving} disabled={!dirty}>
                            Guardar borrador
                          </Button>
                          <Popconfirm
                            title="¿Confirmar grupos? Se generarán los partidos y no podrás modificar el armado."
                            onConfirm={confirmarGrupos}
                            okText="Confirmar"
                            disabled={!inscripcionCerrada}
                          >
                            <Button
                              type="primary"
                              icon={<CheckOutlined />}
                              loading={saving}
                              disabled={!inscripcionCerrada}
                              title={
                                !inscripcionCerrada
                                  ? 'Cerrá las inscripciones del torneo antes de confirmar los grupos'
                                  : undefined
                              }
                            >
                              Confirmar grupos
                            </Button>
                          </Popconfirm>
                        </Space>
                      </Col>
                    )}
                  </Row>

                  {!editableGrupos && (
                    <Alert type="warning" message="Los grupos están confirmados. No se pueden modificar." style={{ marginBottom: 16 }} />
                  )}

                  {editableGrupos && selectedPareja && (
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message={`Pareja seleccionada: ${selectedPareja.etiqueta}. Hacé clic en un grupo para asignarla, o arrastrala.`}
                    />
                  )}

                  <Row gutter={16} align="top">
                    <Col xs={24} md={8}>
                      <div
                        data-drag-scroll
                        style={{
                          position: 'sticky',
                          top: 12,
                          maxHeight: 'calc(100vh - 200px)',
                          overflowY: 'auto',
                          paddingRight: 4,
                        }}
                      >
                        <Title level={5}>
                          Sin asignar ({sinAsignarPagadas.length}
                          {sinAsignarPendientes.length > 0 ? ` · ${sinAsignarPendientes.length} pendientes` : ''})
                        </Title>
                        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
                          Creá la estructura de grupos y asigná solo parejas confirmadas. Las pendientes de pago quedan aparte hasta que abonen.
                        </Text>
                        <ZonaDrop
                          titulo="Listas para asignar"
                          parejas={sinAsignarPagadas}
                          grupoKey="sin-asignar"
                          editable={editableGrupos}
                          onDrop={handleDrop}
                          dragItem={dragItem}
                          setDragItem={setDragItem}
                          onDragOverZone={autoScrollOnDrag}
                          selectedPareja={selectedPareja}
                          onSelectPareja={setSelectedPareja}
                          highlightDrop={Boolean(selectedPareja)}
                        />
                        {sinAsignarPendientes.length > 0 && (
                          <div style={{ marginTop: 16 }}>
                            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
                              Pendientes de pago (no asignables a grupos)
                            </Text>
                            {sinAsignarPendientes.map((p) => (
                              <ParejaCard key={p.tni_id} pareja={p} draggable={false} />
                            ))}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col xs={24} md={16}>
                      <div
                        data-drag-scroll
                        style={{
                          position: 'sticky',
                          top: 12,
                          maxHeight: 'calc(100vh - 200px)',
                          overflowY: 'auto',
                          paddingRight: 4,
                        }}
                      >
                        <Title level={5}>Grupos</Title>
                        <Row gutter={[12, 0]}>
                          {gruposLocal.map((g) => (
                            <Col xs={24} sm={12} lg={8} key={g.tgr_nombre}>
                              <ZonaDrop
                                titulo={`Grupo ${g.tgr_nombre}`}
                                parejas={g.parejas}
                                cupo={g.tgr_cupo}
                                grupoKey={g.tgr_nombre}
                                semilla={g.tgr_semilla}
                                onSemillaChange={editableGrupos ? cambiarSemillaGrupo : null}
                                editable={editableGrupos}
                                onDrop={handleDrop}
                                dragItem={dragItem}
                                setDragItem={setDragItem}
                                onDragOverZone={autoScrollOnDrag}
                                selectedPareja={selectedPareja}
                                onSelectPareja={setSelectedPareja}
                                highlightDrop={Boolean(selectedPareja)}
                              />
                            </Col>
                          ))}
                        </Row>
                        {!gruposLocal.length && (
                          <Text type="secondary">Creá la estructura de grupos con el botón &quot;Crear estructura sugerida&quot;</Text>
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>
              ),
            },
            {
              key: 'fase-grupos',
              label: 'Fase de grupos',
              children: (
                <div>
                  {!resumen?.torneo?.tor_grupos_confirmados ? (
                    <Alert type="info" message="Primero confirmá el armado de grupos." />
                  ) : (
                    <>
                      {faseGrupos?.fase_completa && faseActual === 'FASE_GRUPOS' && (
                        <Alert
                          type="success"
                          message="Todos los partidos de zona están jugados."
                          action={
                            <Button size="small" type="primary" onClick={cerrarFaseGrupos} loading={saving}>
                              Cerrar fase y calcular clasificados
                            </Button>
                          }
                          style={{ marginBottom: 16 }}
                        />
                      )}
                      {(faseGrupos?.grupos || []).map((g) => (
                        <Card key={g.tgr_id} size="small" title={`Grupo ${g.tgr_nombre}`} style={{ marginBottom: 16 }}>
                          {g.formato_cruce_cuatro && (
                            <Alert
                              type="info"
                              showIcon
                              style={{ marginBottom: 12 }}
                              message="Zona de 4 (torneo con grupos de 3): ronda 1 son 2 semifinales; al completarlas se arma ronda 2 con cruce (ganador vs perdedor del otro partido)."
                            />
                          )}
                          <Title level={5} style={{ marginTop: 0 }}>Tabla de posiciones</Title>
                          <Table
                            size="small"
                            pagination={false}
                            rowKey="tni_id"
                            dataSource={g.tabla}
                            columns={columnasTablaGrupo}
                            style={{ marginBottom: 16 }}
                          />
                          <Title level={5}>Partidos</Title>
                          <Table
                            size="small"
                            pagination={false}
                            rowKey="tpg_id"
                            dataSource={g.partidos}
                            columns={[
                              {
                                title: 'Ronda',
                                key: 'ronda',
                                width: 72,
                                render: (_, r) => (r.tpg_ronda === 2 ? '2 (cruce)' : r.tpg_ronda ?? 1),
                              },
                              { title: 'Pareja A', dataIndex: 'pareja_a', key: 'a' },
                              { title: 'Pareja B', dataIndex: 'pareja_b', key: 'b' },
                              {
                                title: 'Resultado',
                                key: 'res',
                                width: 160,
                                render: (_, r) => formatearResultadoTabla(r, 'tpg_estado', r.tpg_sets_a, r.tpg_sets_b),
                              },
                              {
                                title: '',
                                key: 'acc',
                                width: 100,
                                render: (_, r) => (
                                  <Button size="small" onClick={() => abrirResultadoGrupo(r)}>
                                    {r.tpg_estado === 'PENDIENTE' ? 'Cargar' : 'Editar'}
                                  </Button>
                                ),
                              },
                            ]}
                          />
                        </Card>
                      ))}
                    </>
                  )}
                </div>
              ),
            },
            {
              key: 'llave',
              label: 'Llave eliminatoria',
              children: (
                <div>
                  {crucesLocal?.length > 0 && !llave?.llave_generada && (
                    <Card size="small" title="Preview de cuadro eliminatorio" style={{ marginBottom: 16 }}>
                      {preview?.num_byes > 0 && (
                        <Alert
                          type="info"
                          showIcon
                          style={{ marginBottom: 12 }}
                          message={`Cuadro de ${preview.tamano_cuadro} con ${preview.num_byes} bye(s). Los primeros de las mejores semillas avanzan directo.`}
                        />
                      )}
                      <LlaveOctavosEditor
                        cruces={crucesLocal}
                        onChange={handleCrucesChange}
                        rondaLabel={RONDA_LABELS[preview?.primera_ronda] || 'Primera ronda'}
                      />
                      <Divider />
                      <Title level={5} style={{ marginTop: 0 }}>Vista del cuadro</Title>
                      <TorneoBracket
                        porRonda={buildPreviewPorRonda(
                          crucesLocal,
                          preview?.primera_ronda,
                        )?.porRonda}
                        rondaLabels={RONDA_LABELS}
                        preview
                        showChampion={false}
                      />
                      <Divider />
                      <Space wrap>
                        <Button
                          onClick={restaurarCrucesAutomaticos}
                          disabled={!crucesDirty || saving}
                          icon={<ReloadOutlined />}
                        >
                          Restaurar automático
                        </Button>
                        <Popconfirm
                          title="¿Generar la llave con este armado?"
                          onConfirm={generarLlave}
                          okText="Generar"
                        >
                          <Button type="primary" icon={<TrophyOutlined />} loading={saving}>
                            Generar llave
                            {crucesDirty ? ' (editado)' : ''}
                          </Button>
                        </Popconfirm>
                      </Space>
                    </Card>
                  )}

                  {!crucesLocal?.length && !llave?.llave_generada && (
                    <Alert
                      type="info"
                      message="Cerrá la fase de grupos para ver el preview y generar la llave eliminatoria."
                    />
                  )}

                  {llave?.llave_generada && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          {vistaLlave === 'bracket'
                            ? 'Hacé clic en un partido para cargar o editar el resultado.'
                            : 'Vista detallada por ronda.'}
                        </Text>
                        <Segmented
                          value={vistaLlave}
                          onChange={setVistaLlave}
                          options={[
                            { label: 'Árbol', value: 'bracket', icon: <ApartmentOutlined /> },
                            { label: 'Tabla', value: 'table', icon: <UnorderedListOutlined /> },
                          ]}
                        />
                      </div>

                      {vistaLlave === 'bracket' ? (
                        <TorneoBracket
                          porRonda={llave.por_ronda}
                          rondaLabels={RONDA_LABELS}
                          onMatchClick={abrirResultadoLlave}
                        />
                      ) : (
                        Object.entries(llave.por_ronda || {}).map(([ronda, partidos]) => (
                          <Card key={ronda} size="small" title={RONDA_LABELS[ronda] || ronda} style={{ marginBottom: 16 }}>
                            <Table
                              size="small"
                              pagination={false}
                              rowKey="tpl_id"
                              dataSource={partidos}
                              columns={[
                                { title: '#', dataIndex: 'tpl_orden', width: 40 },
                                { title: 'Pareja A', dataIndex: 'pareja_a' },
                                { title: 'Pareja B', dataIndex: 'pareja_b' },
                                {
                                  title: 'Resultado',
                                  width: 160,
                                  render: (_, r) => formatearResultadoTabla(r, 'tpl_estado', r.tpl_sets_a, r.tpl_sets_b),
                                },
                                {
                                  title: 'Ganador',
                                  dataIndex: 'ganador',
                                  render: (v) => v || '-',
                                },
                                {
                                  title: '',
                                  width: 100,
                                  render: (_, r) => (
                                    r.tpl_inscripcion_a_id && r.tpl_inscripcion_b_id ? (
                                      <Button size="small" onClick={() => abrirResultadoLlave(r)}>
                                        {r.tpl_estado === 'PENDIENTE' ? 'Cargar' : 'Editar'}
                                      </Button>
                                    ) : (
                                      <Text type="secondary" style={{ fontSize: 12 }}>A confirmar</Text>
                                    )
                                  ),
                                },
                              ]}
                            />
                          </Card>
                        ))
                      )}

                      {llave.torneo_finalizado && (
                        <Alert type="success" message="¡Torneo finalizado!" showIcon style={{ marginTop: 16 }} />
                      )}
                    </div>
                  )}
                </div>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title="Inscribir pareja manualmente"
        open={modalInscripcionManual}
        onCancel={() => setModalInscripcionManual(false)}
        onOk={guardarInscripcionManual}
        confirmLoading={guardandoInscripcion}
        okText="Inscribir pareja"
        okButtonProps={{ disabled: !okInscripcionManual }}
        destroyOnClose
        width={520}
      >
        <Spin spinning={cargandoJugadores}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Text type="secondary">
              Armá la pareja eligiendo dos jugadores del club. Útil cuando te llaman por teléfono para anotarse.
            </Text>
            <div>
              <Text strong>Jugador 1</Text>
              <Select
                showSearch
                allowClear
                placeholder="Buscar jugador"
                style={{ width: '100%', marginTop: 8 }}
                options={opcionesJugadores.filter((o) => o.value !== jugador2Id)}
                value={jugador1Id}
                onChange={(id) => {
                  setJugador1Id(id);
                  if (!jugadorClubIdCobro) setJugadorClubIdCobro(id);
                }}
                filterOption={filterJugadorOption}
                optionFilterProp="label"
              />
            </div>
            <div>
              <Text strong>Jugador 2</Text>
              <Select
                showSearch
                allowClear
                placeholder="Buscar compañero"
                style={{ width: '100%', marginTop: 8 }}
                options={opcionesJugadores.filter((o) => o.value !== jugador1Id)}
                value={jugador2Id}
                onChange={setJugador2Id}
                filterOption={filterJugadorOption}
                optionFilterProp="label"
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div>
                <Text strong>¿Ya abonó?</Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Si marcás pagado, se registra el cobro en caja (igual que en Cobros).
                  </Text>
                </div>
              </div>
              <Switch
                checked={marcadaPagada}
                onChange={(checked) => {
                  setMarcadaPagada(checked);
                  if (!checked) {
                    setFormaPagoId(null);
                    setJugadorClubIdCobro(null);
                  } else if (!jugadorClubIdCobro && jugador1Id) {
                    setJugadorClubIdCobro(jugador1Id);
                  }
                }}
                checkedChildren="Pagado"
                unCheckedChildren="Pendiente"
              />
            </div>
            {marcadaPagada && (
              <>
                <div>
                  <Text strong>Forma de pago</Text>
                  <Select
                    showSearch
                    allowClear
                    placeholder="Seleccionar forma de pago"
                    style={{ width: '100%', marginTop: 8 }}
                    value={formaPagoId}
                    onChange={(id) => {
                      setFormaPagoId(id);
                      const fp = formasPago.find((f) => f.fop_id === id);
                      const esCC = fp?.fop_nombre?.toUpperCase().trim() === 'CUENTA CORRIENTE';
                      if (!esCC) setJugadorClubIdCobro(jugador1Id || null);
                    }}
                    optionFilterProp="label"
                    options={formasPago.map((fp) => ({
                      value: fp.fop_id,
                      label: fp.fop_nombre,
                    }))}
                  />
                </div>
                {esCuentaCorrientePago && (
                  <div>
                    <Text strong>Jugador cuenta corriente</Text>
                    <Select
                      showSearch
                      allowClear
                      placeholder="A quién se carga la deuda"
                      style={{ width: '100%', marginTop: 8 }}
                      options={opcionesJugadores.filter(
                        (o) => o.value === jugador1Id || o.value === jugador2Id
                      )}
                      value={jugadorClubIdCobro}
                      onChange={setJugadorClubIdCobro}
                      filterOption={filterJugadorOption}
                      optionFilterProp="label"
                    />
                  </div>
                )}
              </>
            )}
          </Space>
        </Spin>
      </Modal>

      <Modal
        title="Registrar resultado"
        open={!!modalResultado}
        onCancel={() => setModalResultado(null)}
        onOk={guardarResultado}
        confirmLoading={saving}
        okText="Guardar"
        okButtonProps={{ disabled: haySetsConError || partidoIncompleto }}
        width={520}
      >
        {modalResultado && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Text type="secondary">
              Sets 1 y 2: games válidos 6-0 a 6-4, 7-5 o 7-6.
              {cantidadSetsTorneo === 3 && (
                superTieBreakTorneo
                  ? ' Set 3: super tie-break a 10 con diferencia de 2.'
                  : ' Set 3: set completo (6-0 a 7-6).'
              )}
            </Text>
            {setsDetalle.map((set) => {
              const validacion = validacionesSetsModal.find((v) => v.numeroSet === set.numeroSet);
              const setCompleto = set.gamesA != null && set.gamesB != null;
              const esSuperTb = esSuperTieBreakSet(set.numeroSet, cantidadSetsTorneo, superTieBreakTorneo);
              return (
              <div key={set.numeroSet}>
                <Text strong>
                  Set {set.numeroSet}
                  {esSuperTb && <Text type="secondary" style={{ fontWeight: 'normal' }}> (super tie-break)</Text>}
                  {cantidadSetsTorneo === 3 && set.numeroSet === 3 && !superTieBreakTorneo && (
                    <Text type="secondary" style={{ fontWeight: 'normal' }}> (set completo)</Text>
                  )}
                </Text>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                  <Text style={{ flex: 1 }}>
                    {modalResultado.partido.pareja_a}
                  </Text>
                  <InputNumber
                    min={0}
                    max={99}
                    value={set.gamesA}
                    onChange={(v) => actualizarSetDetalle(set.numeroSet, 'gamesA', v)}
                    style={{ width: 70 }}
                    status={setCompleto && validacion && !validacion.valido ? 'error' : undefined}
                  />
                  <Text>-</Text>
                  <InputNumber
                    min={0}
                    max={99}
                    value={set.gamesB}
                    onChange={(v) => actualizarSetDetalle(set.numeroSet, 'gamesB', v)}
                    style={{ width: 70 }}
                    status={setCompleto && validacion && !validacion.valido ? 'error' : undefined}
                  />
                  <Text style={{ flex: 1, textAlign: 'right' }}>
                    {modalResultado.partido.pareja_b}
                  </Text>
                </div>
                {setCompleto && validacion && !validacion.valido && (
                  <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                    {validacion.mensaje}
                  </Text>
                )}
              </div>
              );
            })}
            <Divider style={{ margin: '8px 0' }} />
            {partidoIncompleto && (
              <Alert type="warning" showIcon message={validacionPartidoCompleto.mensaje} />
            )}
            <div>
              <Text strong>Resultado en sets: </Text>
              <Text>
                {resumenSetsModal.setsA > 0 || resumenSetsModal.setsB > 0
                  ? `${resumenSetsModal.setsA} - ${resumenSetsModal.setsB}`
                  : '-'}
              </Text>
            </div>
          </Space>
        )}
      </Modal>
    </MainLayout>
  );
};

export default TorneoGestion;
